import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PersonFinal } from '@/api/entities';
import { Calendar, Loader2, User } from 'lucide-react';
import { toast } from 'sonner';

export default function EventsAttendedFilter() {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);

  const fetchPeopleWithEvents = async () => {
    setIsLoading(true);
    try {
      const people = await PersonFinal.filter(
        { "events_attended.0": { "$exists": true } },
        '-updated_at',
        1000
      );
      
      setResults(people);
      toast.success(`Found ${people.length} people with events attended`);
    } catch (error) {
      console.error('Error fetching people:', error);
      toast.error('Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPeopleWithoutEvents = async () => {
    setIsLoading(true);
    try {
      const people = await PersonFinal.filter(
        { 
          "$or": [
            { "events_attended": { "$exists": false } },
            { "events_attended": [] },
            { "events_attended": null }
          ]
        },
        '-updated_at',
        1000
      );
      
      setResults(people);
      toast.success(`Found ${people.length} people without events attended`);
    } catch (error) {
      console.error('Error fetching people:', error);
      toast.error('Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Events Attended Filter
        </CardTitle>
        <CardDescription>
          Filter people by events_attended field (superadmin only)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-3">
          <Button
            onClick={fetchPeopleWithEvents}
            disabled={isLoading}
            className="flex-1"
          >
            {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />}
            Has Events
          </Button>
          <Button
            onClick={fetchPeopleWithoutEvents}
            disabled={isLoading}
            variant="outline"
            className="flex-1"
          >
            {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <User className="w-4 h-4 mr-2" />}
            No Events
          </Button>
        </div>

        {results && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium">Results: {results.length}</span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setResults(null)}
              >
                Clear
              </Button>
            </div>
            
            <div className="border rounded-lg max-h-96 overflow-y-auto">
              {results.length === 0 ? (
                <div className="p-4 text-center text-gray-500">No results found</div>
              ) : (
                <div className="divide-y">
                  {results.map((person) => (
                    <div key={person.id} className="p-3 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-sm">
                            {person.first_name} {person.last_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {person.person_id} • {person.account_name}
                          </div>
                          <Badge variant="secondary" className="mt-1 text-xs">
                            {person.tenant_id}
                          </Badge>
                        </div>
                        <div className="text-right">
                          {person.events_attended && person.events_attended.length > 0 && (
                            <div className="text-xs text-gray-600">
                              {person.events_attended.length} event{person.events_attended.length !== 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      </div>
                      {person.events_attended && person.events_attended.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {person.events_attended.map((event, idx) => (
                            <div key={idx} className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                              {event}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}